import { create } from 'zustand'

export type UploadStatus = 'pending' | 'uploading' | 'completed' | 'failed'

type UploadItem = {
  id: string
  fileName: string
  progress: number
  status: UploadStatus
  abortController?: AbortController
}

export type UploaderStoreState = {
  items: Array<UploadItem>
}

export type UploaderStoreActions = {
  addItem(item: UploadItem): void
  startUpload(id: string): void
  updateProgress(id: string, progress: number): void
  markCompleted(id: string): void
  markFailed(id: string): void
  cancelUpload(id: string): void
  removeItem(id: string): void
  clearCompleted(): void
}

export const useUploaderStore = create<
  UploaderStoreState & UploaderStoreActions
>(set => ({
  items: [],

  addItem: item =>
    set(({ items }) => ({
      items: [...items, item]
    })),

  startUpload: id =>
    set(({ items }) => ({
      items: items.map(item =>
        item.id === id ? { ...item, status: 'uploading', progress: 0 } : item
      )
    })),

  updateProgress: (id, progress) =>
    set(({ items }) => ({
      items: items.map(item =>
        item.id === id ? { ...item, progress, status: 'uploading' } : item
      )
    })),

  markCompleted: id =>
    set(({ items }) => ({
      items: items.map(item =>
        item.id === id ? { ...item, status: 'completed', progress: 100 } : item
      )
    })),

  markFailed: id =>
    set(({ items }) => ({
      items: items.map(item =>
        item.id === id ? { ...item, status: 'failed' } : item
      )
    })),

  cancelUpload: id =>
    set(({ items }) => {
      const item = items.find(i => i.id === id)
      item?.abortController?.abort()
      return {
        items: items.filter(i => i.id !== id)
      }
    }),

  removeItem: id =>
    set(({ items }) => ({
      items: items.filter(item => item.id !== id)
    })),

  clearCompleted: () =>
    set(({ items }) => ({
      items: items.filter(
        item => item.status !== 'completed' && item.status !== 'failed'
      )
    }))
}))
